import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaSearch, FaTimes, FaCheck, FaBuilding, FaUser, FaPhone } from "react-icons/fa";
import "./CompanySelection.css";
import LoadingPage from "../../components/Loading/LoadingPage";
import blockClubLogo from "../../media/blockclub-logo.png";

function CompanySelection() {
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");

  // Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  // Selection states
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Optional profile fields
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  // Submission states
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Debounce timer
  const [debounceTimer, setDebounceTimer] = useState(null);

  useEffect(() => {
    if (!email) {
      navigate("/perks-directory");
    }
  }, [email, navigate]);

  // Debounced search
  useEffect(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    if (searchQuery.trim().length > 0) {
      const timer = setTimeout(() => {
        searchCompanies(searchQuery);
      }, 300);
      setDebounceTimer(timer);
    } else {
      setSearchResults([]);
      setSearchError("");
    }

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const searchCompanies = async (query) => {
    setSearching(true);
    setSearchError("");

    try {
      const response = await fetch(`${apiUrl}/loyalty/search-companies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to search companies");
      }

      const data = await response.json();
      setSearchResults(data.results || []);

      if (data.results.length === 0) {
        setSearchError("No companies found. Try a different search.");
      }
    } catch (error) {
      console.error("Error searching companies:", error);
      setSearchError(error.message || "Failed to search. Please try again.");
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setSubmitError("");
  };

  const handleChangeCompany = () => {
    setSelectedCompany(null);
    setSubmitError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCompany) {
      setSubmitError("Please select your company or place");
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const requestBody = {
        email,
        company_id: selectedCompany.company_id,
      };

      // Add optional fields if provided
      if (name.trim()) {
        requestBody.name = name.trim();
      }

      if (phone.trim()) {
        requestBody.phone_e164 = phone.trim();
      }

      const response = await fetch(`${apiUrl}/loyalty/complete-signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to complete signup");
      }

      const data = await response.json();

      // Store auth token and email in session
      if (data.auth_token) {
        sessionStorage.setItem("loyalty_auth_token", data.auth_token);
      }
      if (data.loyalty_customer_id) {
        sessionStorage.setItem("loyalty_customer_id", data.loyalty_customer_id);
      }
      sessionStorage.setItem("loyalty_email", email);
      sessionStorage.setItem("loyalty_email_verified", new Date().toISOString());

      // Redirect to questionnaire
      navigate(`/loyalty-questionnaire?email=${encodeURIComponent(email)}`);
    } catch (error) {
      console.error("Error completing signup:", error);
      setSubmitError(error.message || "Failed to complete signup. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!email) {
    return <LoadingPage />;
  }

  return (
    <div className="company-selection-wrapper">
      {/* Header */}
      <nav className="company-selection-navbar">
        <div className="company-selection-navbar-logo">
          <img src={blockClubLogo} alt="Block Club logo" />
        </div>
      </nav>

      <div className="company-selection-container">
        <div className="company-selection-header">
          <FaBuilding className="company-selection-icon" />
          <h1>Where are you signing up from?</h1>
          <p className="company-selection-subtitle">
            Select your building, hotel, co-working space, or organization
          </p>
        </div>

        <form onSubmit={handleSubmit} className="company-selection-form">
          {!selectedCompany ? (
            <>
              {/* Search Section */}
              <div className="company-search-section">
                <div className="company-search-bar">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search for your company or place..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="search-clear-btn"
                      onClick={() => {
                        setSearchQuery("");
                        setSearchResults([]);
                        setSearchError("");
                      }}
                      aria-label="Clear search"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>

                {/* Search Results */}
                {searching && (
                  <div className="search-loading">
                    <div className="loading-spinner"></div>
                    <p>Searching...</p>
                  </div>
                )}

                {!searching && searchError && (
                  <div className="search-empty">
                    <FaSearch className="empty-icon" />
                    <p>{searchError}</p>
                  </div>
                )}

                {!searching && searchResults.length > 0 && (
                  <div className="search-results">
                    <p className="results-count">
                      Found {searchResults.length} {searchResults.length === 1 ? 'place' : 'places'}
                    </p>
                    <div className="results-list">
                      {searchResults.map((company) => (
                        <button
                          key={company.company_id}
                          type="button"
                          className="result-item"
                          onClick={() => handleCompanySelect(company)}
                        >
                          <div className="result-logo">
                            {company.logo_url ? (
                              <img src={company.logo_url} alt={company.company_name} />
                            ) : (
                              <FaBuilding className="placeholder-icon" />
                            )}
                          </div>
                          <div className="result-info">
                            <span className="result-name">{company.company_name}</span>
                          </div>
                          <FaCheck className="select-icon" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Selected Company Section */}
              <div className="selected-company-section">
                <div className="selected-company-card">
                  <div className="selected-company-header">
                    <div className="selected-company-logo">
                      {selectedCompany.logo_url ? (
                        <img src={selectedCompany.logo_url} alt={selectedCompany.company_name} />
                      ) : (
                        <FaBuilding className="placeholder-icon" />
                      )}
                    </div>
                    <div className="selected-company-info">
                      <label className="field-label">Selected Place</label>
                      <h3>{selectedCompany.company_name}</h3>
                    </div>
                    <button
                      type="button"
                      className="change-company-btn"
                      onClick={handleChangeCompany}
                    >
                      Change
                    </button>
                  </div>
                </div>

                {/* Optional Profile Fields */}
                <div className="profile-fields">
                  <div className="form-group">
                    <label className="field-label">
                      <FaUser /> Your Name <span className="optional-text">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="field-label">
                      <FaPhone /> Phone Number <span className="optional-text">(Optional)</span>
                    </label>
                    <input
                      type="tel"
                      className="form-input"
                      placeholder="+1 (555) 123-4567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                    <p className="field-hint">Include country code (e.g., +1 for US)</p>
                  </div>
                </div>

                {submitError && (
                  <div className="submit-error">
                    <FaTimes /> {submitError}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  className="complete-signup-btn"
                  disabled={submitting}
                >
                  {submitting ? "Completing Signup..." : "Continue to Preferences"}
                </button>
              </div>
            </>
          )}
        </form>

        {/* Help Text */}
        <div className="company-selection-help">
          <div className="help-content">
            <p className="help-title">🏢 Can't find your place?</p>
            <p className="help-description">
              Help us bring this program to your location! Contact us below and earn referral rewards if we partner with your organization.
            </p>
            <button
              type="button"
              className="contact-us-btn"
              onClick={() => navigate(`/contact-us?email=${encodeURIComponent(email)}&type=referral`)}
            >
              Contact Us About Your Location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanySelection;
